<script lang="ts" setup>
import { ref, onMounted, onUnmounted, computed, watch, ComputedRef } from 'vue';
import orderBy from 'lodash/orderBy';

import { UserIcon } from '@heroicons/vue/solid';
import { PauseIcon, PlayIcon } from '@heroicons/vue/outline';

import { useFilterStore } from '@/stores';
import { RealtimeReportService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import {
    RealtimeReportServiceGetUserDevicesResult,
    UserDeviceType,
    UserDeviceStatus,
    UserModel,
    UserDeviceStateModel,
    AgentExtensionReportGetters,
    AgentExtensionReportActions,
    AgentExtensionReportFilter,
    SortMenuItem,
    SortMenuItemDirection,
    Permission,
    SelectOptionsServiceGetUserLabelsResult,
    FilterItem,
} from '@/types';
import {
    AgentExtensionReportFilters,
    AgentExtensionReportSort,
} from '@/views/pages/reports/agent-extension/components';
import store from '@/store';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// global filter state
const filterStore = useFilterStore();
const levelFilter = computed(() => filterStore.level);
const filter = computed<AgentExtensionReportFilter>(
    () => store.getters[AgentExtensionReportGetters.FILTER],
);
const activeFilters: ComputedRef<FilterItem[]> = computed(
    () => store.getters[AgentExtensionReportGetters.ACTIVE_FILTERS],
);

const search = computed<string>({
    get: () => store.getters[AgentExtensionReportGetters.SEARCH],
    set: (value) => store.dispatch(AgentExtensionReportActions.SET_SEARCH, value),
});

const sortMenuSelected = computed<SortMenuItem[]>({
    get: () => store.getters[AgentExtensionReportGetters.SORT],
    set: (value) => store.dispatch(AgentExtensionReportActions.SET_SORT, value),
});

// Reactive state
const userDevices = ref<RealtimeReportServiceGetUserDevicesResult[]>([]);
const isLoading = ref(false);
const isRunning = ref(false);

type UserDeviceTable = Partial<
    Record<
        UserDeviceType,
        {
            state: UserDeviceStateModel | null;
            status: UserDeviceStatus;
        }
    >
>;

interface UserTableRow {
    user: RealtimeReportUser;
    devices: UserDeviceTable;
}

interface RealtimeReportUser extends UserModel {
    labels?: SelectOptionsServiceGetUserLabelsResult[];
}

const tableHeaders = [
    {
        key: 'name',
        label: 'report.realtimeAgentExtension.details.userName',
        align: 'left',
    },
    {
        key: 'mobile',
        label: 'report.realtimeAgentExtension.details.mobile',
        align: 'center',
    },
    {
        key: 'desktop',
        label: 'report.realtimeAgentExtension.details.desktop',
        align: 'center',
    },
    {
        key: 'web',
        label: 'report.realtimeAgentExtension.details.web',
        align: 'center',
    },
    {
        key: 'external',
        label: 'report.realtimeAgentExtension.details.external',
        align: 'center',
    },
    {
        key: 'queue',
        label: 'report.realtimeAgentExtension.details.queues',
        align: 'center',
    },
];

const deviceTypes = [
    UserDeviceType.MOBILE,
    UserDeviceType.DESKTOP,
    UserDeviceType.WEB,
    UserDeviceType.EXTERNAL,
];

let pollingTimeoutId: number | null = null;

const connectedUserDeviceStatuses = new Set([
    UserDeviceStatus.CONNECTED,
    UserDeviceStatus.DIALING,
    UserDeviceStatus.RING,
    UserDeviceStatus.RINGING,
    UserDeviceStatus.INUSE,
    UserDeviceStatus.IN_CALL,
]);

const computedUserTableRows = computed<UserTableRow[]>(() => {
    const users = new Map<number, UserTableRow>();

    for (const userDevice of userDevices.value) {
        const extensionNumber = userDevice.user.extension!.number;
        let user = users.get(extensionNumber);

        if (!user) {
            user = {
                user: userDevice.user,
                devices: {},
            };

            users.set(extensionNumber, user);
        }

        const status = userDevice.user.queueStatusHistory?.length
            ? UserDeviceStatus.IN_PAUSE
            : (userDevice.state?.status as UserDeviceStatus | undefined) ||
              UserDeviceStatus.OFFLINE;

        user.devices[userDevice.deviceType as UserDeviceType] = {
            state: userDevice.state,
            status,
        };
    }

    let rows = Array.from(users.values());

    if (filter.value.status) {
        rows = rows.filter((row) =>
            Object.values(row.devices).some(
                (device) => device?.status === filter.value.status?.value,
            ),
        );
    }

    if (filter.value.user) {
        rows = rows.filter((row) => row.user.extension?.number === filter.value.user?.id);
    }

    if (filter.value.userLabel) {
        rows = rows.filter((row) =>
            row.user.labels?.some((label) => label.id === filter.value.userLabel?.id),
        );
    }

    if (filter.value.queue) {
        rows = rows.filter((row) =>
            row.user.queues?.some((queue) => queue.id === filter.value.queue?.id),
        );
    }

    if (search.value) {
        const searchValue = search.value.toLowerCase().trim();

        rows = rows.filter(
            (row) =>
                row.user.name.toLowerCase().includes(searchValue) ||
                row.user.extension?.number?.toString().includes(searchValue),
        );
    }

    const sortFields: Record<string, (row: UserTableRow) => string | number> = {
        name: (row) => row.user.name.toLowerCase(),
        extension: (row) => row.user.extension!.number,
    };

    const selectedSorts = sortMenuSelected.value;

    if (selectedSorts.length) {
        return orderBy(
            rows,
            selectedSorts.map((sort) => sortFields[sort.key]),
            selectedSorts.map((sort) =>
                sort.direction === SortMenuItemDirection.ASC ? 'asc' : 'desc',
            ),
        );
    }

    return orderBy(rows, [sortFields.name, sortFields.extension], ['asc', 'asc']);
});

// Called periodically to update real-time data
const fetchData = async () => {
    try {
        isLoading.value = true;
        const usersResp = await RealtimeReportService.getUserDevices();
        userDevices.value = usersResp.data;
    } catch (err) {
        toastServiceError(err);
    } finally {
        isLoading.value = false;
    }
};

// Starts polling every 5s — restarts automatically if still running
const startPolling = async () => {
    isRunning.value = true;
    const loop = async () => {
        if (!isRunning.value) return;
        await fetchData();
        if (isRunning.value) pollingTimeoutId = window.setTimeout(loop, 5000);
    };
    loop();
};

// Stops polling on unmount or when filters change
const stopPolling = () => {
    isRunning.value = false;
    if (pollingTimeoutId) clearTimeout(pollingTimeoutId);
};

// Lifecycle: start/stop polling
onMounted(() => startPolling());
onUnmounted(() => stopPolling());

const toggleQueueMemberPause = async (userId: number) => {
    try {
        const response = await RealtimeReportService.toggleQueueMemberPause(userId);
        toast.success(response.data.message);
        await fetchData();
    } catch (error) {
        toastServiceError(error);
    }
};

const handleRemoveFilter = (filter: FilterItem) => {
    store.dispatch(AgentExtensionReportActions.CLEAR_FILTER, filter.field);
};

// Restart polling on level change — ensures data stays scoped to current filter
watch([levelFilter], () => {
    stopPolling();
    startPolling();
});
</script>

<template>
    <div class="space-y-4 p-4 sm:p-6 lg:p-8">
        <UiPageHeader>
            <template #info>
                <h1 class="flex text-xl font-semibold text-gray-800">
                    {{ $t('report.realtimeAgentExtension.title') }}
                    <UiSpinner v-if="isLoading" class="theme-color ml-2 h-6 w-6" />
                </h1>
            </template>
        </UiPageHeader>
        <div class="space-y-2">
            <UiPanel class="flex px-4 py-2" no-padding>
                <UiListSearch v-model="search" />
                <div class="ml-2 flex items-center space-x-4">
                    <AgentExtensionReportSort v-model="sortMenuSelected" />
                    <AgentExtensionReportFilters />
                </div>
            </UiPanel>
        </div>
        <div
            v-if="activeFilters.length"
            class="mt-4 flex bg-gray-50 px-4 py-4 shadow ring-1 ring-gray-300 sm:p-6 md:rounded"
        >
            <template :key="filter" v-for="filter in activeFilters">
                <UiBadge removable @remove="handleRemoveFilter(filter)">
                    {{ $t(`report.realtimeAgentExtension.filters.activeTag.${filter.field}`) }}
                    {{ filter.label }}
                </UiBadge>
            </template>
        </div>
        <UiPanel no-padding>
            <table class="w-full border-collapse text-sm">
                <thead class="sticky top-0 bg-gray-200 text-gray-800">
                    <tr>
                        <th
                            v-for="header in tableHeaders"
                            :key="header.key"
                            class="p-4"
                            :class="header.align === 'center' ? 'text-center' : 'text-left'"
                        >
                            {{ $t(header.label) }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="row in computedUserTableRows"
                        :key="row.user.id"
                        class="even:bg-gray-100"
                    >
                        <!-- Name -->
                        <td class="align-center p-4">
                            <div class="flex flex-1 items-center">
                                <UserIcon
                                    class="mr-2 h-12 w-12 rounded-full border border-gray-300 bg-gray-50 p-0.5 text-gray-400 shadow"
                                />
                                <div class="flex flex-col">
                                    <div class="font-semibold text-gray-800">
                                        {{ row.user.name }}
                                    </div>
                                    <div class="text-sm text-gray-400">
                                        Ramal {{ row.user.extension?.number }}
                                    </div>
                                </div>
                            </div>
                        </td>
                        <!-- Devices -->
                        <td
                            v-for="deviceType in deviceTypes"
                            :key="deviceType"
                            class="align-center p-4 text-center"
                        >
                            <div
                                class="flex flex-col items-center justify-center space-y-1 px-1 py-2"
                            >
                                <p
                                    v-if="row.devices[deviceType]"
                                    :class="[
                                        connectedUserDeviceStatuses.has(
                                            row.devices[deviceType]?.state
                                                ?.status as UserDeviceStatus,
                                        )
                                            ? 'bg-success-50 border-success-800 text-success-700'
                                            : 'bg-danger-50 border-danger-800 text-danger-700',
                                        'grid grid-cols-1 gap-1 rounded border-2 border-opacity-15 bg-gray-50 px-1 py-0.5 pt-1 text-center text-xs font-semibold',
                                    ]"
                                >
                                    {{
                                        $t(
                                            `report.realtimeAgentExtension.device.status.${
                                                row.user?.queueStatusHistory?.length
                                                    ? UserDeviceStatus.IN_PAUSE
                                                    : row.devices[deviceType]?.status ||
                                                      UserDeviceStatus.OFFLINE
                                            }`,
                                        )
                                    }}
                                </p>
                                <p v-else>-</p>
                            </div>
                        </td>
                        <!-- Queues -->
                        <td class="align-center p-4">
                            <div class="flex justify-center gap-2">
                                <div>
                                    <div
                                        v-for="queue in row.user.queues"
                                        :key="queue.id"
                                        class="flex gap-1"
                                    >
                                        <component
                                            :is="
                                                $can(Permission.VIEW_QUEUE) ? 'RouterLink' : 'span'
                                            "
                                            :to="{
                                                name: 'ViewQueueById',
                                                params: {
                                                    id: queue?.id,
                                                },
                                            }"
                                            class="text-primary"
                                        >
                                            {{ queue.name }}
                                        </component>
                                    </div>
                                </div>
                                <UiActionButton
                                    v-if="row.user.queues?.length && $can(Permission.EDIT_QUEUE)"
                                    variant="primary"
                                    text-variant="primary"
                                    @click="toggleQueueMemberPause(row.user.id)"
                                >
                                    <PauseIcon
                                        v-if="!row.user?.queueStatusHistory?.length"
                                        class="h-7 w-7"
                                    />
                                    <PlayIcon v-else class="h-7 w-7" />
                                    <span>{{
                                        row.user?.queueStatusHistory?.length
                                            ? t('report.realtimeAgentExtension.actions.unpause')
                                            : t('report.realtimeAgentExtension.actions.pause')
                                    }}</span>
                                </UiActionButton>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </UiPanel>
    </div>
</template>
